<template>
    <div>
        <div v-if="displayStatic">
            <div
                v-for="metadataPair in staticMetadata"
                v-bind:key="metadataPair.label"
                v-bind:class="metadataRowClasses">
                <div v-bind:class="metadataLeftColClasses">
                    {{ metadataPair.label }}
                </div>
                <div
                    v-bind:class="metadataRightColClasses"
                    v-bind:data-test-role="metadataPair.testRole">
                    {{ metadataPair.value }}
                </div>
            </div>
        </div>
        <div v-else>
            <div v-bind:class="metadataRowClasses">
                <div v-bind:class="metadataLeftColClasses">
                    {{ fromLabel }}
                </div>
                <div
                    id="MessageFrom"
                    v-bind:class="metadataRightColClasses">
                    {{ messageFrom }}
                </div>
            </div>
            <div v-if="displayReplyToOverride">
                <div v-bind:class="metadataRowClasses">
                    <div v-bind:class="metadataLeftColClasses">
                        <pc-label
                            v-bind:label="replyToDisplayNameLabel"
                            for-id="ReplyNameBox" />
                    </div>
                    <div v-bind:class="metadataRightColClasses">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <pc-input
                                    id="ReplyNameBox"
                                    v-model="messageReplyToName"
                                    v-bind:readonly="readonly || useDefaultReplyToName" />
                            </div>
                            <pc-checkbox
                                id="ReplyToNameCheckBox"
                                v-model="useDefaultReplyToName"
                                v-bind:readonly="readonly"
                                class="ms-2"
                                v-bind:label="useDefaultCheckboxLabel" />
                        </div>
                    </div>
                </div>
                <div v-bind:class="metadataRowClasses">
                    <div v-bind:class="metadataLeftColClasses">
                        <pc-label
                            v-bind:label="replyToEmailLabel"
                            for-id="ReplyEmailBox" />
                    </div>
                    <div v-bind:class="metadataRightColClasses">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <pc-input
                                    id="ReplyEmailBox"
                                    ref="replyToEmail"
                                    v-model="messageReplyToEmail"
                                    v-bind:validation-group-id="validatorKeys.replyToEmail"
                                    v-bind:validator="replyToEmailValidator"
                                    v-bind:show-validation-errors="false"
                                    v-bind:readonly="readonly || useDefaultReplyToEmail"
                                    inputmode="email"
                                    v-on:validated="onValidated(validatorKeys.replyToEmail, $event)" />
                            </div>
                            <pc-checkbox
                                id="ReplyToEmailCheckBox"
                                v-model="useDefaultReplyToEmail"
                                v-bind:readonly="readonly"
                                class="ms-2"
                                v-bind:label="useDefaultCheckboxLabel" />
                        </div>
                        <div
                            v-if="hasValidationError(validatorKeys.replyToEmail)"
                            class="row">
                            <validation-render-helper
                                v-bind:validation-group-id="validatorKeys.replyToEmail"
                                class="mt-0"
                                v-bind:validation-error="validationResults[validatorKeys.replyToEmail].error" />
                        </div>
                    </div>
                </div>
            </div>
            <div v-bind:class="metadataRowClasses">
                <div v-bind:class="metadataLeftColClasses">
                    <pc-label
                        v-bind:label="toLabel"
                        show-required
                        for-id="ToTextBox" />
                </div>
                <div v-bind:class="metadataRightColClasses">
                    <pc-input
                        id="ToTextBox"
                        v-model="messageTo"
                        v-bind:validator="messageToValidator"
                        v-bind:readonly="readonly"
                        inputmode="email"
                        v-on:validated="onValidated(validatorKeys.messageTo, $event)" />
                </div>
            </div>
            <div
                v-if="!isAnnouncement"
                v-bind:class="metadataRowClasses">
                <div v-bind:class="metadataLeftColClasses">
                    <pc-label
                        v-bind:label="ccLabel"
                        for-id="CcTextBox" />
                </div>
                <div v-bind:class="metadataRightColClasses">
                    <pc-input
                        id="CcTextBox"
                        v-model="messageCc"
                        v-bind:validator="messageCcValidator"
                        v-bind:readonly="readonly"
                        inputmode="email"
                        v-on:validated="onValidated(validatorKeys.messageCc, $event)" />
                </div>
            </div>
            <div
                v-if="!isAnnouncement"
                v-bind:class="metadataRowClasses">
                <div v-bind:class="metadataLeftColClasses">
                    <pc-label
                        v-bind:label="bccLabel"
                        for-id="BccTextBox" />
                </div>
                <div v-bind:class="metadataRightColClasses">
                    <pc-input
                        id="BccTextBox"
                        v-model="messageBcc"
                        v-bind:validator="messageBccValidator"
                        v-bind:readonly="readonly"
                        inputmode="email"
                        v-on:validated="onValidated(validatorKeys.messageBcc, $event)" />
                </div>
            </div>
            <div v-bind:class="metadataRowClasses">
                <div v-bind:class="metadataLeftColClasses">
                    <pc-label
                        v-bind:label="subjectLabel"
                        show-required
                        for-id="SubjectTextBox" />
                </div>
                <div v-bind:class="metadataRightColClasses">
                    <pc-input
                        id="SubjectTextBox"
                        v-model="messageSubject"
                        v-bind:validator="messageSubjectValidator"
                        v-bind:readonly="readonly"
                        v-on:validated="onValidated(validatorKeys.messageSubject, $event)" />
                </div>
            </div>
            <div
                v-if="isAnnouncement"
                v-bind:class="metadataRowClasses">
                <div v-bind:class="metadataLeftColClasses">
                    <pc-label
                        v-bind:label="announcementTopicLabel"
                        show-required
                        for-id="TopicDropDownList" />
                </div>
                <div v-bind:class="metadataRightColClassesWithButton">
                    <pc-select
                        id="TopicDropDownList"
                        v-model="communicationTopicId"
                        data-test-role="topic-dropdown-list"
                        v-bind:validator="messageTopicValidator"
                        class="flex-grow-1"
                        v-bind:options="sortedCommunicationTopics"
                        text-prop="TopicName"
                        value-prop="TopicId"
                        v-on:validated="onValidated(validatorKeys.messageTopic, $event)">
                        <option value="">
                            {{ communicationTopicDefaultOptionText }}
                        </option>
                    </pc-select>
                    <pc-button
                        class="ms-2 mb-auto"
                        data-test-role="new-topic-button"
                        v-bind:disabled="!canAddNewTopic"
                        v-on:click="createNewTopic()">
                        {{ createNewTopicButtonLabel }}
                    </pc-button>
                </div>
            </div>
        </div>
        <div v-if="!hideAttachments">
            <div
                v-bind:class="metadataRowClasses"
                class="align-items-baseline">
                <div v-bind:class="metadataLeftColClasses">
                    {{ attachmentsLabel }}
                </div>
                <div v-bind:class="metadataRightColClasses">
                    <div v-if="!hasAttachments">
                        {{ attachmentsNoneText }}
                    </div>
                    <div data-test-role="attachment-list-nonempty">
                        <div
                            v-for="attachmentModel in communicationAttachmentModels"
                            v-bind:key="attachmentModel.PublicId"
                            class="mb-1">
                            <div class="d-flex align-items-center justify-content-between">
                                <a
                                    v-if="
                                        Boolean(attachmentModel.ContentLibraryItemPublicId) &&
                                        Boolean(contentLibraryBaseUrl)
                                    "
                                    v-bind:href="contentUrl(attachmentModel)"
                                    target="_blank">
                                    {{ attachmentModel.Name }}
                                </a>
                                <pc-button
                                    v-else-if="!isTemplateMode"
                                    class="p-0 text-start"
                                    link
                                    data-test-role="attachment-view-link"
                                    v-on:click="onAttachmentClicked(attachmentModel.PublicId)">
                                    {{ attachmentModel.Name }}
                                </pc-button>
                                <div v-else>
                                    {{ attachmentModel.Name }}
                                </div>
                                <pc-button
                                    v-if="!readonly"
                                    small
                                    v-on:click="onDeleteAttachmentClicked(attachmentModel.PublicId)">
                                    {{ attachmentDeleteBtnLabel }}
                                </pc-button>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="!readonly"
                        class="d-flex flex-wrap">
                        <pc-button
                            id="AddAttachmentButton"
                            data-test-role="AddAttachmentButton"
                            v-bind:disabled="!allowMoreAttachments"
                            class="me-2"
                            v-on:click="onAddAttachmentClicked">
                            {{ attachmentAddBtnLabel }}
                        </pc-button>
                        <pc-button
                            id="AddContentItemButton"
                            v-bind:disabled="!allowMoreAttachments"
                            v-on:click="onAddContentItemClicked">
                            {{ contentItemAddBtnLabel }}
                        </pc-button>
                    </div>
                </div>
            </div>
        </div>
        <mfa-dialog
            ref="mfaRequiredDialog"
            v-bind:manage-account-url="manageAccountUrl" />
    </div>
</template>
<script>
    import { NAMESPACE, KEYS } from "./i18n/messageInfo.i18n";
    import { mapI18n } from "Util/i18n";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import validationRenderHelper from "Validation/_validation-error-helper.vue";
    import { ValidationHub } from "Validation/validationHub";
    import { confirmViewSensitiveAttachment } from "Util/clientAttachment";
    import { ROLE_PRIVILEGE } from "Roles/rolePrivileges";
    import mfaDialog from "Account/mfaRequired.vue";
    import addAttachmentDialog from "./addAttachmentDialog.vue";
    import addContentItemDialog from "./addContentItemDialog.vue";
    import { DIALOG_HOOK, DIALOG_RESULT } from "Components/pc-dialog.vue";

    const MAX_TOPIC_COUNT = 20;

    export default {
        components: {
            validationRenderHelper,
            mfaDialog
        },
        provide() {
            return {
                validationHub: this.validationHub
            };
        },
        props: {
            messageInfoInitial: { type: Object, required: false, default: () => {} },
            displayStatic: { type: Boolean, required: false, default: false },
            readonly: { type: Boolean, required: false, default: false },
            hideBcc: { type: Boolean, required: false, default: false },
            hideAttachments: { type: Boolean, required: false, default: false },
            allAttachments: { type: Array, required: false, default: () => [] },
            allContentItems: { type: Array, required: false, default: () => [] },
            communicationTopics: { type: Array, required: false, default: () => [] },
            topicId: { type: String, required: false, default: () => null },

            // type and props for replyToEmail field validator
            replyToEmailValidatorProps: { type: Object, required: false, default: null },
            // type and props for validator used by all other email-type fields
            emailFieldValidatorProps: { type: Object, required: false, default: null },

            addAttachmentValidator: { type: Object, required: false, default: null },

            // Needed in case user wants to download sensitive attachment for mfa-required dialog
            manageAccountUrl: { type: String, required: false, default: null },

            // Needed for linking to unsaved Content Library mappings
            contentLibraryBaseUrl: { type: String, required: false, default: null },

            // True for edit communication template
            isTemplateMode: { type: Boolean, required: false, default: false },
            isAnnouncement: { type: Boolean, required: false, default: false }
        },
        emits: ["attachment-clicked", "message-info-updated", "validated", "create-new-topic"],
        data() {
            return {
                messageFrom: null,

                replyToNameOverride: null,
                replyToEmailOverride: null,
                useDefaultReplyToNameInternal: true,
                useDefaultReplyToEmailInternal: true,

                messageToInternal: null,
                messageCcInternal: null,
                messageBccInternal: null,
                messageSubjectInternal: null,
                communicationTopicIdInternal: null,

                communicationAttachmentModels: [],
                maxNumberOfAttachments: 0,

                validatorKeys: {
                    replyToEmail: "replyToEmail",
                    messageTo: "messageTo",
                    messageCc: "messageCc",
                    messageBcc: "messageBcc",
                    messageSubject: "messageSubject",
                    messageTopic: "messageTopic"
                },
                validationResults: {},
                validationHub: new ValidationHub(),

                metadataRowClasses: "row align-items-center",
                metadataLeftColClasses: "col col-12 col-md-2 fw-bold mb-0 mb-md-3",
                metadataRightColClasses: "col col-12 col-md-10 mb-3",
                metadataRightColClassesWithButton: "col col-12 col-md-10 mb-3 d-flex align-items-end"
            };
        },
        computed: {
            useDefaultReplyToName: {
                get() {
                    return this.useDefaultReplyToNameInternal;
                },
                set(value) {
                    this.useDefaultReplyToNameInternal = value;
                    this.onMessageInfoUpdated();
                }
            },
            useDefaultReplyToEmail: {
                get() {
                    return this.useDefaultReplyToEmailInternal;
                },
                set(value) {
                    this.useDefaultReplyToEmailInternal = value;
                    this.onMessageInfoUpdated();
                }
            },
            messageTo: {
                get() {
                    return this.messageToInternal;
                },
                set(value) {
                    this.messageToInternal = value;
                    this.onMessageInfoUpdated();
                }
            },
            messageCc: {
                get() {
                    return this.messageCcInternal;
                },
                set(value) {
                    this.messageCcInternal = value;
                    this.onMessageInfoUpdated();
                }
            },
            messageBcc: {
                get() {
                    return this.messageBccInternal;
                },
                set(value) {
                    this.messageBccInternal = value;
                    this.onMessageInfoUpdated();
                }
            },
            messageSubject: {
                get() {
                    return this.messageSubjectInternal;
                },
                set(value) {
                    this.messageSubjectInternal = value;
                    this.onMessageInfoUpdated();
                }
            },
            communicationTopicId: {
                get() {
                    return this.communicationTopicIdInternal;
                },
                set(value) {
                    this.communicationTopicIdInternal = value;
                    this.onMessageInfoUpdated();
                }
            },
            displayReplyToOverride() {
                return this.messageInfoInitial.ReplyToOverride;
            },
            replyToEmailValidator() {
                return this.getValidatorIfEditable([this.replyToEmailValidatorProps]);
            },
            messageToValidator() {
                const subValidators = [this.emailFieldValidatorProps];

                const requiredValidator = {
                    type: VALIDATOR_TYPES.required,
                    overrideMsg: this.messageToValidationErrorMessage
                };
                subValidators.push(requiredValidator);

                return this.getValidatorIfEditable(subValidators);
            },
            messageCcValidator() {
                return this.getValidatorIfEditable(this.emailFieldValidatorProps);
            },
            messageBccValidator() {
                return this.getValidatorIfEditable(this.emailFieldValidatorProps);
            },
            messageSubjectValidator() {
                const subValidators = [
                    // This is kind of a hack to ensure that we always treat this input
                    // as having a validator even when the required validator disappears
                    VALIDATOR_TYPES.alwaysValid
                ];

                const requiredValidator = {
                    type: VALIDATOR_TYPES.required,
                    overrideMsg: this.messageSubjectValidationErrorMessage
                };
                subValidators.push(requiredValidator);

                return this.getValidatorIfEditable(subValidators);
            },
            messageTopicValidator() {
                const subValidators = [];

                // we will require a topic even for drafts, because announcements are defined by having a topic
                const requiredValidator = {
                    type: VALIDATOR_TYPES.required,
                    overrideMsg: this.messageTopicValidationErrorMessage
                };
                subValidators.push(requiredValidator);

                return this.getValidatorIfEditable(subValidators);
            },
            isValid() {
                for (const key in this.validationResults) {
                    if (this.validationResults.hasOwnProperty(key)) {
                        const result = this.validationResults[key];
                        if (result.hasError) {
                            return false;
                        }
                    }
                }
                return true;
            },
            availableAttachments() {
                // Filter the list of all attachments to the ones that are not already
                // attached to this communication
                const attached = this.communicationAttachmentModels;
                return this.allAttachments.filter((available) => {
                    return !attached.some((a) => {
                        return a.AttachmentDefinitionPublicId === available.PublicId;
                    });
                });
            },
            unavailableItemIds() {
                return this.communicationAttachmentModels
                    .filter((attached) => Boolean(attached.ContentLibraryItemPublicId))
                    .map((attached) => attached.ContentLibraryItemPublicId);
            },
            hasAttachments() {
                return this.communicationAttachmentModels && this.communicationAttachmentModels.length > 0;
            },
            allowMoreAttachments() {
                return (
                    !this.communicationAttachmentModels ||
                    this.communicationAttachmentModels.length < this.maxNumberOfAttachments
                );
            },
            canUserViewSensitiveAttachments() {
                return this.$app.loggedInUserHasAdminPrivilege(ROLE_PRIVILEGE.ViewSensitiveData);
            },
            messageReplyToName: {
                get() {
                    return !this.useDefaultReplyToName && this.replyToNameOverride != null
                        ? this.replyToNameOverride
                        : this.messageInfoInitial.PlaceHolderReplyToName;
                },
                set(value) {
                    if (!this.useDefaultReplyToName) {
                        this.replyToNameOverride = value;
                    }
                    this.onMessageInfoUpdated();
                }
            },
            messageReplyToEmail: {
                get() {
                    return !this.useDefaultReplyToEmail && this.replyToEmailOverride != null
                        ? this.replyToEmailOverride
                        : this.messageInfoInitial.PlaceHolderReplyToEmail;
                },
                set(value) {
                    if (!this.useDefaultReplyToEmail) {
                        this.replyToEmailOverride = value;
                    }
                    this.onMessageInfoUpdated();
                }
            },
            sortedCommunicationTopics() {
                const sortedCommunicationTopics = this._clone(this.communicationTopics);
                sortedCommunicationTopics.sort((a, b) => {
                    return a.TopicName.localeCompare(b.TopicName);
                });
                return sortedCommunicationTopics;
            },
            staticMetadata() {
                const staticMetadata = [
                    { label: this.fromLabel, value: this.messageFrom },
                    { label: this.toLabel, value: this.messageTo },
                    { label: this.ccLabel, value: this.messageCc },
                    { label: this.subjectLabel, value: this.messageSubject }
                ];
                // Add Bcc if appropriate (will only contain project owner if they are among Bcc recipients)
                const bccIndex = 3;
                if (!this.hideBcc) {
                    staticMetadata.splice(bccIndex, 0, {
                        label: this.bccLabel,
                        value: this.messageBcc,
                        testRole: "bcc-text"
                    });
                }
                return staticMetadata;
            },
            updatedInfo() {
                // In template mode, if we're using a default reply-to email or display name,
                // emit empty string so we don't end up saving the default value. If a template
                // is using the default reply to values, it should just store "" in those fields.
                // Otherwise every template would have to be updated if we ever update the default
                // values for the program.
                const replyToName = this.isTemplateMode && this.useDefaultReplyToName ? "" : this.messageReplyToName;

                const replyToEmail = this.isTemplateMode && this.useDefaultReplyToEmail ? "" : this.messageReplyToEmail;

                return {
                    MessageReplyToDisplayName: replyToName,
                    MessageReplyToEmail: replyToEmail,
                    MessageTo: this.messageTo,
                    MessageCc: this.messageCc,
                    MessageBcc: this.messageBcc,
                    MessageSubject: this.messageSubject,
                    AttachmentsOrDocuments: this.communicationAttachmentModels,
                    CommunicationTopicId: this.communicationTopicId
                };
            },
            canAddNewTopic() {
                return this.communicationTopics.length < MAX_TOPIC_COUNT;
            },
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        watch: {
            async useDefaultReplyToEmail() {
                await this.$nextTick();
                if (this.displayReplyToOverride && this.$refs.replyToEmail) {
                    this.$refs.replyToEmail.validate();
                }
            },
            topicId() {
                // this watches for whenever we add a new topic from the edit communication template page
                // and updates the topic id to that new topic
                this.communicationTopicId = this.topicId;
            }
        },
        created() {
            this._initializeFromMessageInfo(this.messageInfoInitial);
        },
        methods: {
            async onAttachmentClicked(publicId) {
                if (this.isTemplateMode) {
                    this.$emit("attachment-clicked", publicId);
                    return;
                }

                const attachment = this.communicationAttachmentModels.find((a) => {
                    return a.PublicId === publicId;
                });
                if (attachment.AttachmentDefinitionIsSensitive) {
                    if (!this.canUserViewSensitiveAttachments) {
                        this.$app.showDialog({
                            title: this.cannotDownloadSensitiveAttachmentDialogHeader,
                            message: this.cannotDownloadSensitiveAttachmentDialogText,
                            buttons: [
                                {
                                    text: this.cannotDownloadSensitiveAttachmentDialogCloseBtnLabel
                                }
                            ]
                        });
                        return;
                    } else if (!(await confirmViewSensitiveAttachment(this))) {
                        return;
                    }
                }

                this.$emit("attachment-clicked", publicId);
            },
            async onAddAttachmentClicked() {
                const { action, data } = await this.$app.showDialog({
                    title: this.addAttachmentDialogHeader,
                    small: false,
                    customComponent: addAttachmentDialog,
                    customComponentOptions: {
                        isTemplateMode: this.isTemplateMode,
                        availableAttachments: this.availableAttachments,
                        addAttachmentValidator: this.addAttachmentValidator
                    },
                    customComponentResultHook: DIALOG_HOOK.getResult,
                    customComponentIsValidToCloseHookAsync: DIALOG_HOOK.isValidToClose,
                    buttons: [
                        {
                            text: this.addAttachmentDialogCancel,
                            result: DIALOG_RESULT.cancel
                        },
                        {
                            primary: true,
                            text: this.addAttachmentDialogOk,
                            result: DIALOG_RESULT.ok
                        }
                    ]
                });
                if (action === DIALOG_RESULT.cancel) {
                    return;
                }

                this._addAttachmentOk(data);
            },
            _addAttachmentOk(attachmentIdToAdd) {
                const attachment = this.availableAttachments.find((a) => {
                    return a.PublicId === attachmentIdToAdd;
                });
                this.communicationAttachmentModels.push(attachment);

                this.onMessageInfoUpdated();
            },
            async onAddContentItemClicked() {
                const { action, data } = await this.$app.showDialog({
                    title: this.addAttachmentDialogHeader,
                    small: false,
                    customComponent: addContentItemDialog,
                    customComponentOptions: {
                        allItems: this.allContentItems,
                        unavailableItemIds: this.unavailableItemIds
                    },
                    customComponentResultHook: DIALOG_HOOK.getResult,
                    customComponentIsValidToCloseHookAsync: DIALOG_HOOK.isValidToClose,
                    buttons: [
                        {
                            text: this.addAttachmentDialogCancel,
                            result: DIALOG_RESULT.cancel
                        },
                        {
                            primary: true,
                            text: this.addAttachmentDialogOk,
                            result: DIALOG_RESULT.ok
                        }
                    ]
                });
                if (action === DIALOG_RESULT.cancel) {
                    return;
                }

                this._addContentItemOk(data);
            },
            _addContentItemOk(contentItemIdToAdd) {
                const item = this.allContentItems.find((i) => {
                    return i.Id === contentItemIdToAdd;
                });
                this.communicationAttachmentModels.push({
                    Name: `${this.contentItemLabel}${item.Name}`,
                    PublicId: item.Id,
                    ContentLibraryItemPublicId: item.Id,
                    ContentLibraryItemIsPublic: item.IsPublic,
                    ContentLibraryItemContentPath: item.ContentPath,
                    AttachmentDocumentFileName: item.Name
                });

                this.onMessageInfoUpdated();
            },
            onDeleteAttachmentClicked(publicId) {
                this.communicationAttachmentModels = this.communicationAttachmentModels.filter((a) => {
                    return a.PublicId !== publicId;
                });
                this.onMessageInfoUpdated();
            },
            onMessageInfoUpdated() {
                this.$app.setUnsavedChanges(true);
                this.$emit("message-info-updated", this.updatedInfo);
            },
            onValidated(key, result) {
                // Must clone and set entire validationResults object to trigger
                // dependency updates
                const updatedResults = this._clone(this.validationResults);
                updatedResults[key] = result;
                this.validationResults = updatedResults;

                this.$emit("validated", this.isValid);
            },
            hasValidationError(key) {
                if (this.readonly) {
                    return false;
                }
                return this.validationResults[key] && this.validationResults[key].hasError;
            },
            getValidatorIfEditable(validatorOptions) {
                return this.readonly ? null : getValidator(this.$app, validatorOptions);
            },
            getAsPlainHtmlForPrint(sentByInfo) {
                /* eslint-disable */
                const attachmentsStr =
                    this.communicationAttachmentModels.length == 0
                        ? ""
                        : this.communicationAttachmentModels
                              .map((a) => {
                                  return a.AttachmentDocumentFileName;
                              })
                              .join("; ");
                return `
                    <div>
                        <div style="float: right;">${this.printCommunicationDateLabel} ${new Date().toString()}</div>
                        <br />
                        <br />
                        ${sentByInfo} <br />
                        ${this.fromLabel} ${this.messageFrom} <br />
                        ${this.toLabel} ${this.messageTo} <br />
                        ${this.replyToEmailLabel} ${this.messageReplyToEmail} <br />
                        ${this.replyToDisplayNameLabel} ${this.messageReplyToName} <br />
                        ${this.ccLabel} ${this.messageCc} <br />
                        ${this.bccLabel} ${this.messageBcc} <br />
                        ${this.subjectLabel} ${this.messageSubject} <br />
                        ${this.attachmentsLabel} ${attachmentsStr} <br />
                    </div>
                    `;
                /* eslint-enable */
            },
            createNewTopic() {
                this.$emit("create-new-topic");
            },
            _initializeFromMessageInfo() {
                this.messageFrom = this.messageInfoInitial.CommSender;

                this.replyToNameOverride = this.messageInfoInitial.MessageReplyToDisplayName;
                this.replyToEmailOverride = this.messageInfoInitial.MessageReplyToEmail;
                this.useDefaultReplyToNameInternal =
                    !this.messageInfoInitial.MessageReplyToDisplayName ||
                    this.messageInfoInitial.MessageReplyToDisplayName ===
                        this.messageInfoInitial.PlaceHolderReplyToName;
                this.useDefaultReplyToEmailInternal =
                    !this.messageInfoInitial.MessageReplyToEmail ||
                    this.messageInfoInitial.MessageReplyToEmail === this.messageInfoInitial.PlaceHolderReplyToEmail;

                this.messageToInternal = this.messageInfoInitial.MessageTo;
                this.messageCcInternal = this.messageInfoInitial.MessageCc;
                this.messageBccInternal = this.messageInfoInitial.MessageBcc;
                this.messageSubjectInternal = this.messageInfoInitial.MessageSubject;

                this.communicationAttachmentModels = this._clone(this.messageInfoInitial.AttachmentsOrDocuments);
                this.maxNumberOfAttachments = this.messageInfoInitial.MaxNumberOfAttachments;

                // if the communication topic id is not already set and this is an announcement
                // then set the topic id to the first topic in the list
                let hasChanges = false;
                if (this.isAnnouncement) {
                    if (this.messageCcInternal !== null && this.messageCcInternal.length !== 0) {
                        this.messageCcInternal = "";
                        hasChanges = true;
                    }
                    if (this.messageBccInternal !== null && this.messageBccInternal.length !== 0) {
                        this.messageBccInternal = "";
                        hasChanges = true;
                    }
                    this.communicationTopicIdInternal = this.messageInfoInitial.CommunicationTopicId;
                } else {
                    if (this.messageInfoInitial.CommunicationTopicId !== null) {
                        hasChanges = true;
                    }
                    this.communicationTopicIdInternal = null;
                }
                if (hasChanges) {
                    this.$emit("message-info-updated", this.updatedInfo);
                }
            },
            // Hook for parent to update message info contents dynamically.
            // Parent component must modify messageInfoInitial before calling this
            async reinitialize() {
                this._initializeFromMessageInfo();
            },
            validateAll() {
                this.validationHub.validate();
            },
            _clone(obj) {
                return JSON.parse(JSON.stringify(obj));
            },
            contentUrl(item) {
                const protection = item.ContentLibraryItemIsPublic ? "Public" : "Program";
                const path = `${this.contentLibraryBaseUrl}/${protection}/${item.ContentLibraryItemContentPath}`;
                if (path.startsWith("https://localhost")) {
                    return path + "?ProgramId=" + this.$app.ProgramId;
                }
                return path;
            }
        }
    };
</script>
